import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Globe, Check, Loader2, Info, ExternalLink, ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

/**
 * InternationalPayoutsCard
 * ------------------------
 * Surfaces on the creator dashboard *only* when the creator's country
 * isn't supported by Stripe Connect destination payouts. Replaces the
 * standard "Connect Stripe" button with a Stripe Global Payouts setup
 * flow that captures bank/Wise virtual-account details.
 *
 * Architecture: Stripe is still the system of record + money mover. The
 * platform doesn't custody funds long-term. This is the same money
 * architecture as Connect, just using a different Stripe API rail
 * (Outbound Payments instead of destination charges).
 */
export default function InternationalPayoutsCard({ onSetupComplete }) {
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    country: '',
    legal_name: '',
    bank_country: 'US',
    account_number: '',
    routing_number: '',
    wise_email: '',
  });

  const load = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get('/creator/payout-eligibility', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEligibility(res.data);
      if (res.data?.country) {
        setForm((f) => ({ ...f, country: res.data.country }));
      }
    } catch {
      // Silently — creator profile may not exist yet, in which case
      // the dashboard already shows the "create creator profile" CTA.
      setEligibility(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async () => {
    if (!form.country || !form.legal_name || !form.account_number) {
      toast.error('Country, legal name, and account number are required');
      return;
    }
    if (form.bank_country.toUpperCase() === 'US' && !form.routing_number) {
      toast.error('Routing number is required for US bank accounts (incl. Wise USD virtual)');
      return;
    }
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const res = await axios.post(
        '/creator/international-payouts/setup',
        {
          country: form.country.toUpperCase(),
          legal_name: form.legal_name.trim(),
          bank_country: form.bank_country.toUpperCase(),
          account_number: form.account_number.trim(),
          routing_number: form.routing_number.trim() || null,
          wise_email: form.wise_email.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || 'International payouts ready');
      setOpen(false);
      await load();
      if (onSetupComplete) onSetupComplete();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Setup failed';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading or no eligibility info — render nothing
  if (loading || !eligibility) return null;

  // Stripe Connect IS supported in their country → render nothing.
  // The standard "Connect Stripe" UI elsewhere on the dashboard handles them.
  if (eligibility.stripe_connect_supported) return null;

  // Setup already complete — show a verified-state pill
  if (eligibility.international_setup_complete) {
    return (
      <div
        data-testid="intl-payouts-complete"
        className="rounded-xl border border-[#00FF94]/30 bg-[#00FF94]/5 p-4 text-sm"
      >
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 flex-shrink-0 text-[#00FF94]" />
          <div className="flex-1">
            <div className="font-semibold text-[#00FF94]">
              International payouts ready
            </div>
            <div className="mt-0.5 text-xs text-white/60">
              Your earnings will be paid via Stripe Global Payouts to your
              bank account ending in ••••{' '}
              <span className="font-mono text-white/80">
                {/* last4 not surfaced through eligibility endpoint —
                    creator can re-open the form to view it */}
              </span>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpen(true)}
            className="border-white/20 bg-transparent text-white/80 hover:bg-white/5"
          >
            Update
          </Button>
        </div>
      </div>
    );
  }

  // Not supported by Global Payouts either (rare — would be a sanctioned
  // country or sub-region). Render an informational dead-end.
  if (!eligibility.global_payouts_supported) {
    return (
      <div
        data-testid="intl-payouts-unsupported"
        className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200/90"
      >
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-400" />
          <div>
            <strong className="text-amber-100">Payouts unavailable in your region.</strong>{' '}
            We're not yet able to send payouts to {eligibility.country || 'your country'}.
            Email <a href="mailto:support@theailedger.ai" className="underline">support@theailedger.ai</a>{' '}
            and we'll explore manual options for you.
          </div>
        </div>
      </div>
    );
  }

  // Main case: Connect not supported but Global Payouts IS — show CTA
  return (
    <>
      <div
        data-testid="intl-payouts-card"
        className="overflow-hidden rounded-xl border border-[#00F0FF]/30 bg-gradient-to-br from-[#00F0FF]/5 to-transparent p-5"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-[#00F0FF]/15 p-2 text-[#00F0FF]">
              <Globe className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-white">
                Set up International Payouts
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-white/60">
                Stripe Connect doesn't currently support creator payouts in{' '}
                <strong className="text-white/80">
                  {eligibility.country || 'your country'}
                </strong>
                . We've got you covered — Stripe Global Payouts can send your
                earnings to a local bank account or a Wise virtual USD account
                in your name. Same Stripe-grade reliability, same auto-payouts,
                no manual handoffs.
              </p>
            </div>
          </div>
          <Button
            data-testid="intl-payouts-setup-button"
            onClick={() => setOpen(true)}
            className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90"
          >
            Set up payouts
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto bg-neutral-950 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>International payout setup</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 text-sm">
            <p className="text-white/60">
              Pro tip: if you have a{' '}
              <a
                href="https://wise.com/business"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[#00F0FF] hover:underline"
              >
                Wise Business account <ExternalLink className="h-3 w-3" />
              </a>
              , use the USD virtual bank account it gives you (free with every
              Wise account) — Stripe pays in USD via ACH, Wise auto-converts to
              your local currency in your Wise balance. Best of both worlds.
            </p>

            <Field
              testId="intl-country"
              label="Your country (ISO 2-letter code)"
              hint="e.g. ID for Indonesia, VN for Vietnam, PK for Pakistan"
              value={form.country}
              onChange={(v) => setForm({ ...form, country: v })}
              placeholder="ID"
              maxLength={2}
            />
            <Field
              testId="intl-legal-name"
              label="Legal name on the bank account"
              value={form.legal_name}
              onChange={(v) => setForm({ ...form, legal_name: v })}
              placeholder="As it appears on your ID"
            />

            <div className="rounded-lg border border-white/10 bg-neutral-900 p-4">
              <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-white/50">
                Bank / Wise virtual account
              </div>
              <Field
                testId="intl-bank-country"
                label="Bank account country (ISO 2-letter)"
                hint="Use US for Wise USD virtual. Otherwise the country of your local bank."
                value={form.bank_country}
                onChange={(v) => setForm({ ...form, bank_country: v })}
                placeholder="US"
                maxLength={2}
              />
              <Field
                testId="intl-account-number"
                label="Account number"
                value={form.account_number}
                onChange={(v) => setForm({ ...form, account_number: v })}
                placeholder="123456789"
              />
              {form.bank_country.toUpperCase() === 'US' && (
                <Field
                  testId="intl-routing-number"
                  label="Routing number (US ACH only)"
                  value={form.routing_number}
                  onChange={(v) => setForm({ ...form, routing_number: v })}
                  placeholder="021000021"
                />
              )}
            </div>

            <Field
              testId="intl-wise-email"
              label="Wise email (optional, for our records)"
              hint="Helps us identify you if Stripe ever needs us to verify your account"
              value={form.wise_email}
              onChange={(v) => setForm({ ...form, wise_email: v })}
              placeholder="you@example.com"
            />

            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200/90">
              <strong className="text-amber-100">A note on timing:</strong>{' '}
              Stripe is approving our Global Payouts access right now. Save this
              setup today — you'll be able to upload products immediately, and
              your payouts will start processing automatically as soon as
              approval lands (typically 1-2 business days).
            </div>
          </div>

          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-white/20 bg-transparent text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              data-testid="intl-payouts-submit"
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90 disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Save & continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const Field = ({ testId, label, hint, value, onChange, placeholder, maxLength }) => (
  <div className="mb-3 last:mb-0">
    <Label className="mb-1 block text-xs uppercase tracking-wider text-white/50">
      {label}
    </Label>
    <Input
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className="bg-neutral-900 border-white/10 text-white placeholder:text-white/30"
    />
    {hint && <p className="mt-1 text-xs text-white/40">{hint}</p>}
  </div>
);
